// Free-tier gate: enforces "1 lifetime credit, with ad required to use it".
//
// Rule (Android native only):
//   • Free user = credits_purchased_total = 0 AND running on Android native.
//   • 1 starter credit granted at signup is the LIFETIME allowance — no daily reset.
//   • Counted by lifetime tryons rows (server truth; user can't clear localStorage to bypass).
//
//      prior tryons  | action
//      --------------+--------------------
//      0             | watch-ad             (this generation = #1 of 1, ad first)
//      1+            | trial-used           (lifetime cap hit → paywall)
//
//   • Paid users (purchased > 0) → always allow.
//   • Web / iOS → always allow (no ads infrastructure on those platforms).
//
// The ad is a GATE, not a reward — it doesn't grant a credit. The user already has
// the credit; the ad just unlocks permission to spend it.
import { supabase } from "@/integrations/supabase/client";
import { isAndroidApp } from "./platform";

export type GateAction = "allow" | "watch-ad" | "trial-used";

export interface GateResult {
  action: GateAction;
  /** 0-based: how many lifetime tryons this user already has. */
  priorCount: number;
  /** 1-based: which generation this would be (1, 2, 3, or >3). */
  generationNumber: number;
}

const ALLOW: GateResult = { action: "allow", priorCount: 0, generationNumber: 1 };

export async function checkFreeTierGate(userId: string): Promise<GateResult> {
  // Off-platform → no gate.
  if (!isAndroidApp()) return ALLOW;

  try {
    // Paid users skip the gate entirely.
    const { data: profile } = await supabase
      .from("profiles")
      .select("credits_purchased_total, credits")
      .eq("user_id", userId)
      .maybeSingle();
    const purchased = Number(profile?.credits_purchased_total ?? 0);
    if (purchased > 0) return ALLOW;

    const balance = Number(profile?.credits ?? 0);

    // Count lifetime tryons (any kind: tryon, outfit_only, aura).
    const { count } = await supabase
      .from("tryons")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId);

    const prior = count ?? 0;
    const next = prior + 1;

    // First-ever generation → ad gates the single starter credit.
    if (prior === 0) {
      return { action: "watch-ad", priorCount: prior, generationNumber: next };
    }

    // Already used the starter credit, but has fresh credits (rewarded ad
    // grant or refund from a failed generation) → allow without re-gating.
    if (balance > 0) return { action: "allow", priorCount: prior, generationNumber: next };

    // No credits and starter is spent → real paywall.
    return { action: "trial-used", priorCount: prior, generationNumber: next };
  } catch (err) {
    // If the check itself fails (offline, network), fail OPEN so we never
    // hard-block a legitimate user. They still need a credit to proceed.
    console.warn("[free-tier-gate] check failed, allowing:", err);
    return ALLOW;
  }
}
